import React, { useState, useEffect, useCallback } from 'react';
import { SelectionData, Player, PlayerWallPostResponse } from '../Model';
import { restClient } from '../RestClient';
import { useReferenceData } from '../ReferenceDataContext';
import { PageLoading } from '../PageLoading';
import { YouTubeEmbed } from '../YouTubeEmbed';
import { getDifficultyColor } from '../Drills/drillColors';

interface PlayerTrainingProps {
  onError: (message: string, type?: 'error' | 'warning' | 'info') => void;
}

export function PlayerTraining({ onError }: PlayerTrainingProps) {
  const { selectedSelectionId, currentPlayerId } = useReferenceData();

  const [selectionData, setSelectionData] = useState<SelectionData | null>(null);
  const [wallPosts, setWallPosts] = useState<PlayerWallPostResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchData = useCallback(async () => {
    if (!selectedSelectionId || !currentPlayerId) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const [selectionResult, wallResult] = await Promise.all([
        restClient.getSelectionData(selectedSelectionId),
        restClient.getPlayerWallPosts(selectedSelectionId, currentPlayerId),
      ]);

      if (selectionResult.IsSuccess) {
        setSelectionData(selectionResult.Value);
      } else {
        onError(`${selectionResult.Error.code}: ${selectionResult.Error.description}`);
      }

      if (wallResult.IsSuccess) {
        setWallPosts(wallResult.Value);
      } else {
        onError(`${wallResult.Error.code}: ${wallResult.Error.description}`);
      }
    } catch (error) {
      console.error('Error loading player training data:', error);
      onError('Unexpected error occurred while loading training data');
    } finally {
      setIsLoading(false);
    }
  }, [selectedSelectionId, currentPlayerId, onError]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (isLoading) {
    return <PageLoading />;
  }

  if (!selectedSelectionId || !currentPlayerId) {
    return (
      <div className="player-training-page p-6">
        <h1 className="text-xl font-semibold mb-4">Training</h1>
        <div className="card">
          <div className="card-body text-center text-muted">
            No selection available.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="player-training-page p-6">
      <div className="mb-6">
        <h1 className="text-xl font-semibold mb-2">Training</h1>
      </div>

      {/* Player Card */}
      {selectionData && (
        <section className="mb-8">
          <PlayerSummaryCard selectionData={selectionData} playerId={currentPlayerId} drillCount={wallPosts.length} lastUpdated={wallPosts[0]?.createdAt} />
        </section>
      )}

      {/* Wall Posts */}
      <section>
        <h2 className="text-lg font-semibold mb-5">Your Drills</h2>
        {wallPosts.length === 0 ? (
          <div className="card">
            <div className="card-body text-center text-muted">
              No posts yet. Your coach will share drills here.
            </div>
          </div>
        ) : (
          <div className="player-wall-grid">
            {wallPosts.map(post => (
              <WallPostCard key={post.id} post={post} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function getAreasToWorkOn(player: Player): string[] {
  const entries = Object.entries(player.skills ?? {});
  if (entries.length === 0) return [];

  // Always show the lowest 3 skills — these are the areas to focus on
  return entries
    .sort(([, a], [, b]) => a - b)
    .slice(0, 3)
    .map(([name]) => name);
}

function PlayerSummaryCard({ selectionData, playerId, drillCount, lastUpdated }: { selectionData: SelectionData; playerId: number; drillCount: number; lastUpdated?: string }) {
  const player = selectionData.players.find(p => p.id === playerId);

  if (!player) {
    return (
      <div className="card">
        <div className="card-body text-center text-muted">
          Player not found in this selection.
        </div>
      </div>
    );
  }

  const team = selectionData.teams.find(t =>
    t.players?.some(p => p.id === playerId)
  );

  const areasToWorkOn = getAreasToWorkOn(player);

  const formattedLastUpdated = lastUpdated
    ? new Date(lastUpdated).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
    : null;

  const playerInitial = player.name.charAt(0).toUpperCase();

  return (
    <div className="card player-training-header">
      <div className="player-training-name-row">
        <span className="player-training-avatar">{playerInitial}</span>
        <div className="player-training-name-col">
          <div className="player-training-name-line">
            <h2>{player.name}</h2>
            <span className="badge badge-sm badge-secondary">{selectionData.sport}</span>
          </div>
          {team && (
            <div className="player-view-team">
              <div className="flex items-center gap-2">
                <span
                  className="player-view-team-dot"
                  style={{ backgroundColor: team.color }}
                />
                <span className="text-sm font-semibold">{team.name}</span>
              </div>
            </div>
          )}
          <div className="player-training-meta">
            {drillCount} saved {drillCount === 1 ? 'drill' : 'drills'}{formattedLastUpdated && ` \u00B7 Updated ${formattedLastUpdated}`}
          </div>
        </div>
      </div>
      {areasToWorkOn.length > 0 && (
        <div className="mt-3">
          <h3 className="text-sm font-semibold mb-2">Recommended focus areas</h3>
          <div className="flex flex-wrap gap-2">
            {areasToWorkOn.map(skill => (
              <span key={skill} className="badge badge-sm badge-accent">{skill}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function WallPostCard({ post }: { post: PlayerWallPostResponse }) {
  const [showVideo, setShowVideo] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const difficultyColors = getDifficultyColor(post.drill.difficulty);
  const formattedDate = new Date(post.createdAt).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

  const coachInitial = post.coachName.charAt(0).toUpperCase();

  return (
    <div className="card player-wall-card">
      <div className="card-body">
        {/* Coach Row */}
        <div className="player-wall-coach-row">
          <span className="player-wall-coach-avatar">{coachInitial}</span>
          <span className="text-sm font-semibold">{post.coachName}</span>
          <span className="player-wall-coach-date">{formattedDate}</span>
        </div>

        {/* Coach Comment */}
        <div className="player-wall-note">
          <span className="player-wall-note-label">Coach Note</span>
          <p className="player-wall-comment">{post.comment}</p>
        </div>

        {/* Drill Info */}
        <div className="player-wall-drill">
          <h3 className="font-semibold mb-2">{post.drill.name}</h3>
          <div className="flex items-center gap-2 flex-wrap mb-3">
            <span
              className="badge badge-sm"
              style={{
                backgroundColor: difficultyColors.bg,
                color: difficultyColors.text,
                border: `1px solid ${difficultyColors.border}`,
              }}
            >
              {post.drill.difficulty}
            </span>
            <span className="badge badge-sm badge-secondary">{post.drill.duration} min</span>
            <span className="badge badge-sm badge-secondary">{post.drill.category}</span>
          </div>

          {/* Skills */}
          {post.drill.skills.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {post.drill.skills.map(skill => (
                <span key={skill} className="badge badge-sm badge-accent">{skill}</span>
              ))}
            </div>
          )}

          {/* Description */}
          {post.drill.description && (
            <>
              <p className={expanded ? undefined : 'player-wall-description'}>{post.drill.description}</p>
              <button className="player-wall-show-more" onClick={() => setExpanded(!expanded)}>
                {expanded ? 'Show less' : 'Show more'}
              </button>
            </>
          )}

          {/* YouTube Video */}
          {post.drill.youtubeUrl && !showVideo && (
            <button className="btn btn-sm btn-outline-primary" onClick={() => setShowVideo(true)}>
              Watch Video
            </button>
          )}
          {post.drill.youtubeUrl && showVideo && (
            <div className="player-wall-video-container">
              <YouTubeEmbed
                url={post.drill.youtubeUrl}
                title={`${post.drill.name} - training video`}
                borderRadius={6}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
